import React, { Component, createRef, Fragment } from 'react';
import { Dropdown } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import AdvancedFormat from 'dayjs/plugin/updateLocale';
import classnames from 'classnames';
import { KeyCode, DateUtil, DateFunction } from './util';
// import 'antd/dist/antd.css';

import './style/PickerInput.less';
import 'antd/es/dropdown/style/css.js';

import Panel from './PickerPanel.js';
import RangePanel from './RangePanel.js';

dayjs.extend(customParseFormat);
dayjs.extend(AdvancedFormat);

function toArray(value) {
    return Array.isArray(value) ? value : [value];
}

const INVALID_DATE_STRING = 'Invalid Date';
const STANDARD_DATE_FORMAT = 'YYYY-MM-DD';
const STANDARD_TIME_FORMAT = 'HH:mm:ss';
const STANDARD_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';

class BasePicker extends Component {
    static defaultProps = { 
        format: ['YYYY-MM-DD'],
        pickerCount: 1,
        separator: <span className='nc-date-picker-separator'>~</span>,
        functions: {},
        allowClear: true,
        autoOpenWhenFocus: true,
    }

    defaultHotKeys = {
        open: 'enter',
        close: 'enter',
        clear: 'del',
    }

    pickerRef = createRef(null);
    panelRef = createRef(null);
    // 一次操作过程中，激活过(focused)的input数量
    selection = new Set();

    constructor (props) {
        super(props);

        this.state = { 
            open: false, 
            mode: this.generateDefaultArray(props.picker === 'dateTime' ? 'date' : props.picker),
            value: this.generateDefaultArray(''), // 非受控时，datepicker的值
            inputValue: this.generateDefaultArray(''), // 输入时，datepicker的值
            hoverValue: null, // 鼠标悬浮时，datepicker的值
            panelValue: [...new Array(props.pickerCount)].map((_, index) => this.toString(this.addDateByHighLevel(dayjs(props.base), index))), // 面板的值
            functionValue: [],
            focusIndex: 0,
            isFocus: false, 
            isHover: false,
            focusInPanel: false,
        };
    }

    componentDidMount() {
        let [values] = this.getRealProperties(['value']);
        this.syncInnerValue(values);
        document.addEventListener('mousedown', this.handleGlobalMouseDown);
        document.addEventListener('mouseup', this.handleGlobalMouseUp);
    }
    
    componentDidUpdate(prevProps, prevState) {
        let { value, picker, onOpen, onClose } = this.props;
        // if (this.props.hasOwnProperty('value') && !this.isEqualArray(value, prevProps.value)) {
        //     this.syncInnerValue(value);
        // }
        if (picker !== prevProps.picker) {
            this.setState({ mode: this.generateDefaultArray(picker === 'dateTime' ? 'date' : picker) });
        }
        if (prevState.open !== this.state.open) {
            this.state.open ? onOpen?.() : onClose?.();
        }
    }

    UNSAFE_componentWillReceiveProps(newProps){
        if (newProps.hasOwnProperty('value') && (!this.isEqualArray(newProps.value, this.props.value) || !this.isEqualArray(newProps.value, this.state.value))||this.state.inputValue=='') {
            this.syncInnerValue(newProps.value);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleGlobalMouseDown);
        document.removeEventListener('mouseup', this.handleGlobalMouseUp);
    }

    render() {
        let { inputValue, focusIndex, panelValue, mode, focusInPanel, functionValue, isFocus } = this.state;
        let { 
            separator, 
            picker, 
            pickerCount, 
            renderExtraFooter, 
            functions, 
            locale, 
            allowClear, 
            format, 
            weekStart, 
            showHour,
            showMinute,
            showSecond,
            dropdownClassName, 
            pickerInputProps,
            pickerPanelProps,
            pickerWrapperProps,
            className,
            disabled,
            style, 
            disabledDate,
            disabledTime,
            base,
            otherPickerProps,
            showInput = true,
            superPrev,
            prev,
            superNext,
            next,
            renderCellContent,
            renderInputIcon,
            showToday,
        } = this.props;
        let [open] = this.getRealProperties(['open']);
        let range = pickerCount > 1;
        let inputProps = this.getInputProperties();

        let selectedValue = this.format(inputValue, format)
            .map(value => this.toDate(value));
        panelValue = panelValue.map(value => this.toDate(value));

        /* 此处限定往panel传入的prop避免过多的无用属性传递，新增属性时在下面对象中添加，不要...props */
        let commonProps = {
            picker,
            onPanelChange: this.handlePanelChange,
            onSelect: this.handleSelect,
            onChange: this.handleChange,
            onDateMouseEnter: this.handleDateMouseEnter,
            onDateMouseLeave: this.handleDateMouseLeave,
            functions,
            renderExtraFooter,
            locale,
            ref: this.panelRef,
            functionValue,
            open,
            weekStart,
            showHour,
            showMinute,
            showSecond,
            pickerPanelProps,
            disabledDate,
            disabledTime,
            base,
            otherPickerProps,
            superPrev,
            prev,
            superNext,
            next,
            renderCellContent,
            showToday,
        };

        return showInput ? <Dropdown
            visible={open}
            placement='bottomLeft'
            overlayClassName={classnames(dropdownClassName, {
                'nc-date-picker-dropdown': true,
                'panel-focused': focusInPanel,
            })}
            overlay={<div 
                onMouseDownCapture={event => {
                    event.preventDefault();
                }}
            >
                {range ? 
                    <RangePanel
                        {...commonProps}
                        mode={mode} 
                        value={selectedValue} // 转成Date的value
                        viewDate={panelValue}
                        focusIndex={focusIndex}
                        functionValue={functionValue}
                    /> : 
                    <Panel 
                        {...commonProps}
                        mode={mode[focusIndex]} 
                        value={selectedValue[focusIndex]} // 转成Date的value
                        viewDate={panelValue[focusIndex]}
                        functionValue={functionValue[focusIndex]}
                    />}
            </div>}
        >
            <div 
                className={classnames(className, {
                    'nc-date-picker': true,
                    'picker-focused': isFocus && !focusInPanel, // focusIndex被修改了，所以从state里取
                    'picker-disabled': disabled,
                })} 
                ref={this.pickerRef}
                onClick={event => {
                    if (event.target === event.currentTarget) {
                        this.pickerRef.current?.querySelector('input')?.focus();
                        this.handleMouseDown(event, 0);
                    }
                }}
                onClickCapture={event => {
                    disabled && event.stopPropagation();
                }}
                onMouseDownCapture={event => {
                    disabled && event.stopPropagation();
                }}
                style={style}
                {...pickerWrapperProps}
            >
                {renderInputIcon && <span className="input-icon">{renderInputIcon()}</span>}
                {inputProps.map((props, index) => {
                    return <Fragment
                        key={index}
                    >
                        {index > 0 && separator}
                        <input 
                            {...pickerInputProps?.[index]}
                            type="text"
                            className={range ? 'range-input': 'single-input'}
                            {...props}
                        />
                    </Fragment>;
                })}
                {allowClear && (inputValue.some(e => e) || functionValue.some(e => functions[e])) && <i className="iconfont icon-qingkong" onClick={this.handleClear} onMouseDown={e => e.preventDefault()}></i>}
                {!renderInputIcon && (<span 
                    className={"iconfont ".concat(picker === 'time' ? "icon-shijian" : "icon-rilizuixin")}
                    onClick={event => {
                        this.pickerRef.current?.querySelector('input')?.focus();
                        this.handleMouseDown(event, 0);
                    }} 
                    onMouseDown={e => e.preventDefault()}
                />)}
            </div>
        </Dropdown> : (
            range ? 
                <RangePanel
                    {...commonProps}
                    mode={mode} 
                    value={selectedValue} // 转成Date的value
                    viewDate={panelValue}
                    focusIndex={focusIndex}
                    functionValue={functionValue}
                /> : 
                <Panel 
                    {...commonProps}
                    mode={mode[focusIndex]} 
                    value={selectedValue[focusIndex]} // 转成Date的value
                    viewDate={panelValue[focusIndex]}
                    functionValue={functionValue[focusIndex]}
                />
        );
    }

    /**
     * 1、选择的是面板 => 更新inputValue、联动panelValue
     * 2、选择的是函数 => 更新functionValue、格式化inputValue、inputValue为空时重置panelValue为当前时间
     * 3、选择的是日期面板 => 清空functionValue
     * @param {Date | functionString} date 
     * @param {picker} type 
     */
    handleSelect = (date, type) => {
        let { onSelect, picker, format } = this.props;

        date = this.toString(date);

        this.setState(({ focusIndex, inputValue, functionValue, panelValue }) => {
            this.selection.add(focusIndex);

            let dateFunction = '';
            if (this.includeDateFunction(date)) {
                dateFunction = date;
                date = this.toDate(
                    this.format(
                        this.translateDateFunction(date, { index: focusIndex }),
                        format.concat(STANDARD_DATE_FORMAT),
                    )[0],
                );
                // 补全时分秒
                let formattedInputValue = this.format(inputValue[focusIndex], format)[0],
                    now = formattedInputValue ? this.toDate(formattedInputValue) : new Date();

                if (date) {
                    date.setHours(now.getHours());
                    date.setMinutes(now.getMinutes());
                    date.setSeconds(now.getSeconds());
    
                    date = this.toString(date);
                } else {
                    date = '';
                }
            }

            // 只有当前选择的面板（type）是最终面板（picker）时，才更新inputValue和functionValue
            // 如：picker === 'dateTime'时，选月（type === month）不应触发更新
            // 用正则，忽略大小写（或者全转小写，用indexOf）
            let shouldUpdate = new RegExp(type, 'i').test(picker);

            if (shouldUpdate) {
                return { 
                    functionValue: this.setValueToArraySafely(functionValue, focusIndex, dateFunction),
                    inputValue: this.setValueToArraySafely(inputValue, focusIndex, date),
                    panelValue: this.generatePanelValue(
                        this.setValueToArraySafely(panelValue, focusIndex, date),
                    ), // 点击当月之外的其他日期时，需要切换面板
                };
            } else {
                return { 
                    panelValue: this.generatePanelValue(
                        this.setValueToArraySafely(panelValue, focusIndex, date),
                    ), // 点击当月之外的其他日期时，需要切换面板
                };
            }
        });

        onSelect?.(date);
    }

    /**
     * 单个面板选择结束时触发
     * 1、点击底部确定按钮时，没有参数，取state中的值
     * 2、否则根据参数更新state
     * 3、只有dateTime和time类型，会没有date
     * 4、date是functionString时 => 更新functionValue
     * 5、date是Date时 => 更新inputValue、清空functionValue
     * @param {Date | functionString} date 
     */
    handleChange = () => {
        let { pickerCount } = this.props;

        this.setState(({ focusIndex, inputValue, functionValue }) => {
            this.selection.add(focusIndex);

            // TODO:参数控制是否可以只选一个
            let finished = this.selection.size === pickerCount;
            
            if (finished) {
                let mergedValue = this.getMergedValue(inputValue, functionValue);
                this.triggerChange(mergedValue);
            } else {
                // requestAnimationFrame为了让onBlur时拿到正确的inputValue
                // 即：先触发select再触发blur
                requestAnimationFrame(() => {
                    this.jumpToNext();
                });
            }

            return {
                open: finished ? false : true,
                hoverValue: '',
                isHover: false,
            };
        });
    }

    handlePanelChange = (nextMode, date, index = 0) => {
        this.setState(({ mode, panelValue, focusIndex }) => {
            return {
                panelValue: this.generatePanelValue(
                    this.setValueToArraySafely(
                        panelValue, 
                        index === undefined ? focusIndex : index, 
                        this.toString(date),
                    ),
                ),
                mode: this.setValueToArraySafely(mode, index, nextMode),
            };
        });
    }

    /**
     * 1、输入时清空日期函数
     * 2、合法值时面板联动
     */
    handleInputChange = (event, index) => {
        let newValue = event.target.value,
            { format } = this.props;
        
        this.setState(({ inputValue, panelValue, functionValue }) => {
            let [formattedValue] = this.format(newValue, format);
            if (formattedValue) {
                // 合法值时，面板联动；否则不变
                panelValue = this.generatePanelValue(
                    this.setValueToArraySafely(panelValue, index, formattedValue),
                );
            }
            return { 
                inputValue: this.setValueToArraySafely(inputValue, index, newValue),
                functionValue: this.setValueToArraySafely(functionValue, index, ''),
                panelValue,
                isHover: false,
            };
        });
    }

    handleFocus = (event, index) => {
        let { isFocus } = this.state,
            { autoOpenWhenFocus, onFocus } = this.props;

        this.setState({ focusIndex: index, isFocus: true, focusInPanel: false });
        autoOpenWhenFocus && this.setState({ open: true });
        
        if (!isFocus) {
            onFocus?.(event);
        }
    }

    handleBlur = (event, index) => {
        requestAnimationFrame(() => {
            let { inputValue, functionValue } = this.state,
                { format, onBlur, pickerCount } = this.props;
            if (this.pickerRef.current?.contains(document.activeElement)) {
                // 内部失焦：切换输入框
                // 格式化当前输入框的值
                if (functionValue[index]) {
                    let [currentValue] = this.format(inputValue[index], format);
                    this.setState({ inputValue: this.setValueToArraySafely(inputValue, index, currentValue) });
                }
            } else {
                // 组件失焦：结束选择
                this.hotKeyBlur = false;
                
                // 触发onChange
                let formattedDate = this.format(inputValue, format),
                    mergedValue = this.getMergedValue(formattedDate, functionValue);
    
                if(pickerCount === 2){
                    let testFunList = ['#past#', '#future#'];
                    if(testFunList.indexOf(mergedValue[0]) !== -1 && testFunList.indexOf(mergedValue[0]) !== -1){ //至过去和至未来同时存在时
                        mergedValue = ["", ""];
                    } else if(!mergedValue[0] && mergedValue[1]){ //有一个为空
                        if(this.isAllowEmptyDateFun('#past#')){
                            mergedValue[0] = '#past#';
                        } else {
                            mergedValue[1] = '';
                        }
                    } else if(mergedValue[0] && !mergedValue[1]){ 
                        if(this.isAllowEmptyDateFun('#future#')){
                            mergedValue[1] = '#future#';
                        } else {
                            mergedValue[0] = '';
                        }
                    }
                }

                // TODO:参数控制是否可以空选
                this.triggerChange(mergedValue);
                this.setState({ open: false, focusIndex: 0, isFocus: false });
                onBlur?.(event);
            }
        });

        // TODO:怕上面的实现方式不稳定，先留着
        if (this.outerClick || this.hotKeyBlur) {
            // 组件失焦：结束选择
        } else {
            // 内部失焦：切换输入框
        }
    }

    handleClear = () => {
        this.triggerChange(this.generateDefaultArray(''));
    }
    
    handleMouseDown = (event, index) => {
        this.selection.add(index);
        this.setState({ open: true, focusInPanel: false });
    }

    handleGlobalMouseDown = e => {
        // 只处理左键
        if (e.button === 0 && !this.pickerRef.current.contains(e.target)) {
            this.outerClick = true;
        }
    }

    handleGlobalMouseUp = () => {
        this.outerClick = false;
    }

    handleDateMouseEnter = date => {
        date = this.toString(date);
        this.setState({
            isHover: true,
            hoverValue: date,
        });
    }

    handleDateMouseLeave = () => {
        this.setState({
            isHover: false,
            hoverValue: '',
        });
    }

    handleKeyDown = event => {
        const { focusInPanel, open } = this.state;
        switch (event.which) {
            case KeyCode.TAB:
                let { focusIndex } = this.state,
                    { pickerCount } = this.props;
                if (event.shiftKey) {
                    if (focusIndex === 0) {
                        this.hotKeyBlur = true;
                    } else if(focusIndex > 0){ //ctrl+tab 切换至上一个面板
                        this.setState({ focusIndex: focusIndex - 1 });
                    }
                } else {
                    if (focusIndex === pickerCount - 1) {
                        this.hotKeyBlur = true;
                    }
                }
                if(focusInPanel){
                    this.setState({ focusInPanel: false });
                }
                break;
            case KeyCode.CTRL:
                if(!open){
                    this.setState({ open: true, focusInPanel: true });
                }
                break;
            case KeyCode.ESC:
                if(open){
                    event.stopPropagation();
                    this.setState({ open: false, focusInPanel: false });
                }
                break;
            case KeyCode.DOWN: //下时进入面标，或者是面板的下
                if(focusInPanel){
                    this?.panelRef?.current?.onKeyDown(event);
                } else if (open) {
                    this.setState({ focusInPanel: true });
                }
                break;
            case KeyCode.ENTER:
                if(focusInPanel){
                    this.setState({ open: false, focusInPanel: false });
                }
            // eslint-disable-next-line no-fallthrough
            case KeyCode.HOME:
            case KeyCode.END:
            case KeyCode.PAGE_UP:
            case KeyCode.PAGE_DOWN:
            case KeyCode.UP:
            case KeyCode.LEFT:
            case KeyCode.RIGHT: //当前聚焦在面板上时，调用面板的按键逻辑处理
                if(focusInPanel){
                    this?.panelRef?.current?.onKeyDown(event);
                }
                break;
            default:
                break;
        }

        if (!focusInPanel) {
            this.props.onKeyDown?.(event);
        } else {
            event.stopPropagation();
        }
    }
    isAllowEmptyDateFun =string=>{
        const { emptyFunList } = this.props;
        if(typeof emptyFunList === 'boolean' && emptyFunList){
            return true;
        }
        if(Array.isArray(emptyFunList) && emptyFunList.indexOf(string) !== -1){
            return true;
        }
        return false;
    }

    // 触发this.props.onChange
    triggerChange = nextValue => {
        nextValue.sort((next, prev) => {
            if (!next || !prev) {
                // 空位不换位
                return 1;
            }
            // 函数的比较
            prev = this.translateDateFunction(prev, { index: 0, key: 'valueOf' });
            next = this.translateDateFunction(next, { index: 1, key: 'valueOf' });

            return next - prev;
        });
        this.selection.clear();
        let [prevValue] = this.getRealProperties(['value']);
        this.syncInnerValue(nextValue);
        if (!this.isEqualArray(prevValue, nextValue)) {
            let { onChange } = this.props;
            onChange?.(nextValue);
        }
    }

    generateDefaultArray = value => {
        // value是基本类型值
        return [...new Array(this.props.pickerCount)].fill(value);
    }

    isEqualArray = (arr1, arr2) => {
        if (arr1.length !== arr2.length) {
            return false;
        }
        return arr1.every((item, index) => item === arr2[index]);
    }

    isAfterDateByHighLevel = (date, baseDate, type) => {
        switch(type){
            case 'date':
            case 'dateTime':
                type = 'month';
                break;
            case 'month':
                type = 'year';
                break;
            case 'year':
                type = 'decade';
                break;
            case 'decade':
                let startYearOfDate = DateUtil.getDecadeYear(date);
                let startYearOfBase = DateUtil.getDecadeYear(baseDate);
                return DateUtil.isAfter(startYearOfDate, startYearOfBase, 'decade');
            default:
                break;
        }
        return DateUtil.isAfter(date, baseDate, type);
    }

    isSameDateByHighLevel = (date, baseDate, type) => {
        switch(type){
            case 'date':
            case 'dateTime':
                type = 'month';
                break;
            case 'month':
                type = 'year';
                break;
            case 'year':
                type = 'decade';
                break;
            case 'decade':
                let startYearOfDate = DateUtil.getDecadeYear(date);
                let startYearOfBase = DateUtil.getDecadeYear(baseDate);
                return DateUtil.isSame(startYearOfDate, startYearOfBase, 'decade');
            default:
                break;
        }
        return DateUtil.isSame(date, baseDate, type);
    }

    getInputProperties = () => {
        let { hoverValue, focusIndex, isHover, inputValue, functionValue, isFocus } = this.state;
        let { placeholder, pickerCount, disabled, autoFocus } = this.props;
        let [values] = this.getRealProperties(['value']);
        return [...new Array(pickerCount)].map((_, index) => {
            
            let value = this.getValueFromArraySafely(values, index);

            /**
             * input中显示什么值，有如下几种状态：
             * 状态1：组件失去为获取焦点时，显示props.value
             * 状态2：组件获取焦点时，显示state.inputValue
             * 状态3：hover组件时，显示state.hoverValue
             */
            let display;
    
            if (isHover && focusIndex === index) {
                display = hoverValue;
            } else if (isFocus) {
                // TODO:手输的函数不翻译
                display = this.getMergedValue(inputValue[index], functionValue[index])[0];
                // 函数翻译
                display = this.translateDateFunction(display, { index, key: functionValue[index] ? 'display' : 'value' });
            } else if (value) {
                // 函数翻译
                display = this.translateDateFunction(value, { index, key: 'display' });
            } else {
                display = '';
            }
            return {
                value: display,
                placeholder: this.getValueFromArraySafely(placeholder, index),
                // placeholder: isFocus ? '选择日期' : this.getValueFromArraySafely(placeholder, index),
                onChange: event => this.handleInputChange(event, index),
                onMouseDown: event => this.handleMouseDown(event, index),
                onFocus: event => this.handleFocus(event, index),
                onBlur: event => this.handleBlur(event, index),
                onKeyDown: this.handleKeyDown,
                disabled,
                autoFocus: index === 0 ? autoFocus : false,
            };
        });
    }

    getValueFromArraySafely = (value, index) => {
        return Array.isArray(value) ? value[index]: '';
    }

    setValueToArraySafely = (originValue, index, value) => {
        if (!Array.isArray(originValue)) {
            originValue = this.generateDefaultArray('');
        } 
        let clone = [...originValue];
        clone[index] = value;
        return clone;
    }

    getRealProperties = properties => {
        return properties.map(property => {
            if (this.props.hasOwnProperty(property)) {
                // 受控
                return this.props[property];
            } else {
                // 非受控
                return this.state[property];
            }
        });
    }

    // 格式匹配时，返回标准值
    format = (strings, formats) => {
        strings = toArray(strings);
        formats = toArray(formats);
        return strings.map(string => {
            if(!string) return string;
            if(DateUtil.isUnSupportFormat(formats)){ //有MMM这种不识别的格式特殊处理
                let [unSupportFormat, ...other] = formats;                
                let day = DateUtil.getUnSupportFormatDate(string, unSupportFormat);
                if(day?.isValid()) return day.format(unSupportFormat);

                day = Array.isArray(other) && other.length > 0 ? dayjs(string, other, true) : day;
                return day?.isValid() ? day.format(formats[0]) : '';
            }

            let day = dayjs(string, formats, true);
            // 确保和某一项严格匹配
            if (day.isValid()) {
                return day.format(formats[0]);
            } 
            return '';
        });
    }

    // date => string
    toString = date => {
        if (typeof date === 'string') return date;
        let format = toArray(this.props.format);
        return date ? dayjs(date).format(format[0]) : '';
    }

    // string => date
    toDate = string => {
        if(!string) return null;
        if (string instanceof Date) return string; 
        let format = toArray(this.props.format);
        if(DateUtil.isUnSupportFormat(format[0])){
            let [unSupportFormat, ...other] = format;                
            let day = DateUtil.getUnSupportFormatDate(string, unSupportFormat);
            if(day?.isValid()){
                return day.toDate();
            } else if(Array.isArray(other) && other.length > 0){
                return dayjs(string, other[0]).toDate();
            } else {
                return null;
            }
        }
        let date = dayjs(string, format[0]);
        if(!date.isValid()){
            date = dayjs(string, [...format, STANDARD_DATE_FORMAT, STANDARD_DATE_TIME_FORMAT ]);
        }
        return date.toDate();
    }

    /**
     * props.value发生变化时，同步内部的其他value
     * value中可能包含函数，需要拆开
     * @param {string[]} value 
     */
    syncInnerValue = value => {
        if(value==undefined) return;
        let functionValue = this.generateDefaultArray(''),
            inputValue = this.generateDefaultArray(''),
            // inputValue = this.state.inputValue,
            { format } = this.props;
        value.forEach((value, index) => {
            let [ functionName, date ] = this.getSplittedValue(value);
            if (functionName) {
                functionValue[index] = functionName;

                if (date) {
                    // 翻译
                    let translatedDate = this.toDate(
                            this.format(
                                this.translateDateFunction(functionName, { index }), 
                                format.concat(STANDARD_DATE_FORMAT),
                            )[0],
                        ),
                        // 拼接时分秒
                        inputDate = this.toDate(
                            this.format(
                                date, 
                                format.concat(STANDARD_TIME_FORMAT),
                            )[0],
                        );

                    if (translatedDate) {
                        translatedDate.setHours(inputDate.getHours()); 
                        translatedDate.setMinutes(inputDate.getMinutes()); 
                        translatedDate.setSeconds(inputDate.getSeconds()); 
    
                        inputValue[index] = this.toString(translatedDate);
                    } else {
                        inputValue[index] = '';
                    }
                } else {
                    inputValue[index] = this.translateDateFunction(functionName, { index });
                }

            } else {
                inputValue[index] = this.format(date, format)[0];
            }
        });
        this.setState({
            value,
            inputValue,
            functionValue,
            panelValue: this.generatePanelValue(inputValue),
        });
    }

    /**
     * 根据props.value生成panelValue
     * @param {string []} value 
     * @returns 
     */
    generatePanelValue = value => {
        let { format, picker, pickerCount, base } = this.props, 
            { focusIndex } = this.state,
            panelValue = this.format(value, format);
        if (panelValue.every(e => !!e)) {
            // 都有值，左右面板与开始/结束值同步
            panelValue = panelValue.map(value => this.toDate(value));
            if (this.isAfterDateByHighLevel(this.addDateByHighLevel(panelValue[0], 1), panelValue[1], picker)) {
                if(focusIndex === 0 || this.isSameDateByHighLevel(panelValue[0], panelValue[1], picker)){
                    panelValue[1] = this.addDateByHighLevel(panelValue[0], 1);
                } else {
                    panelValue[0] = this.addDateByHighLevel(panelValue[1], -1);
                }
            }
        } else if (panelValue.every(e => !e)) {
            // 都没值，面板值不变
            // panelValue = this.state.panelValue;
            panelValue = [...new Array(pickerCount)].map((_, index) => this.addDateByHighLevel(dayjs(base), index));
        } else if (panelValue[0]) {
            // 只有左值，以左值为准
            let baseDate = this.toDate(panelValue[0]);
            panelValue = [baseDate, this.addDateByHighLevel(baseDate, 1)];
        } else if (panelValue[1]) {
            // 只有右值，以右值为准
            let baseDate = this.toDate(panelValue[1]);
            panelValue = [this.addDateByHighLevel(baseDate, -1), baseDate];
        }
        return panelValue.map(value => this.toString(value));
    }

    // 根据picker类型不同，修改不同的属性
    addDateByHighLevel = (date, diff) => {
        let { picker } = this.props;
        switch (picker) {
            case 'date':
                return DateUtil.addMonth(date, diff);
            case 'dateTime':
                return DateUtil.addMonth(date, 0);
            case 'month':
                return DateUtil.addYear(date, diff);
            case 'year':
                return DateUtil.addDecade(date, diff);
            default:
                break;
        }
    }

    jumpToNext = () => {
        // 使下一个input聚焦
        let { focusIndex } = this.state,
            { pickerCount } = this.props;
        this.pickerRef.current.querySelectorAll('input')[(focusIndex + 1) % pickerCount]?.focus();
    }

    /**
     * 将string中的#...#翻译为具体日期
     * key: 'value' | 'display' | 'valueOf'
     */
    translateDateFunction = (string, { index = 0, key = 'value' } = {}) => {
        if (!string) {
            return '';
        }

        let { functions, format, displayFormat } = this.props;

        let matched = this.matchedDateFunction(string);

        if (matched) {
            let functionName = matched[1], translatedValue;
    
            if (!functions[functionName]) {
                return '';
            }

            if (key === 'valueOf') {
                // 用于onChange时比较大小
                if (functions[functionName].hasOwnProperty('valueOf')) {
                    return toArray(functions[functionName].valueOf())[index];
                } else {
                    translatedValue = toArray(functions[functionName].value())[index];
                    translatedValue = string.replace(functionName, translatedValue);
                    return +this.toDate(translatedValue);
                }
            } else {
                translatedValue = functions[functionName].value();
                let isSingle = true;
                if (Array.isArray(translatedValue)) {
                    isSingle = false;
                    translatedValue[index] = string.replace(functionName, translatedValue[index]);
                } else {
                    translatedValue = string.replace(functionName, translatedValue);
                }
    
                // TODO:函数时的value和display暂不区分
                if (key ==='value') {
                    // 真实日期
                    return isSingle ? translatedValue : translatedValue[index];
                } else {
                // 显示日期
                    let translatedDisplay = functions[functionName].display(translatedValue);
                    return toArray(translatedDisplay)[index];
                }
            }

        } else {
            if (key === 'valueOf') {
                // 用于onChange时比较大小
                return +this.toDate(string);
            } else if (key === 'value') {
                // 真实日期
                return string;
            } else {
                // 显示日期
                string = this.format(string, format)[0] || INVALID_DATE_STRING; //string; //
                typeof displayFormat === 'function' && (string = displayFormat(string));
                return string;
            }
        }
    }

    matchedDateFunction = string => string.match(/(#[^#]*#)/);

    includeDateFunction = string => /(#[^#]*#)/.test(string);

    getMergedValue = (inputValue, functionValue) => {
        inputValue = toArray(inputValue);
        functionValue = toArray(functionValue);
        let { picker, format, functions } = this.props;

        return inputValue.map((value, index) => {
            if (functionValue[index]) {
                if (picker === 'dateTime' && functions[functionValue[index]]?.type !== 'dateTime') {
                    return this.format(value, [`[${functionValue[index]}] HH:mm:ss`].concat(format))[0] || `${functionValue[index]} 00:00:00`;
                } else {
                    return functionValue[index];
                }
            } else {
                return value;
            }
        });
    }

    getSplittedValue = mergedValue => {
        if (this.includeDateFunction(mergedValue)) {
            return mergedValue.split(' ');
        } else {
            return ['', mergedValue];
        }
    }
}

class RangePicker extends Component {
    render() {
        let props = { ...this.props };

        if (this.props.hasOwnProperty('value')) {
            props.value = toArray(this.props.value);
            props.value.length = 2;
            props.value = [...props.value].map(e => e || '');
        } 
        if (this.props.hasOwnProperty('defaultValue')) {
            props.defaultValue = toArray(this.props.defaultValue);
            props.defaultValue.length = 2;
            props.defaultValue = [...props.defaultValue].map(e => e || '');
        } 
        return <BasePicker 
            {...props} 
            pickerCount={2}
        />;
    }
}

class SinglePicker extends Component {
    handleChange = rangeValue => {
        let [singleValue] = rangeValue;
        this.props.onChange?.(singleValue);
    }

    render() {
        let { value, placeholder, defaultValue } = this.props;

        let props = { ...this.props };

        if (this.props.hasOwnProperty('value')) {
            props.value = toArray(value || '');
        }
        if (this.props.hasOwnProperty('defaultValue')) {
            props.defaultValue = toArray(defaultValue || '');
        }

        return <BasePicker 
            {...props} 
            pickerCount={1} 
            placeholder={toArray(placeholder)}
            onChange={this.handleChange}
        />;
    }
}

export { RangePicker };
export default SinglePicker;